import { useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import colors from "@styling/colors";
import { spacing } from "@styling/sizing";
import { fontSizes } from "@styling/fonts";
import {
  SelectChevron,
  SelectEmpty,
  SelectList,
  SelectOptionButton,
  SelectRoot,
} from "./SelectInput";
import { useSelectListBox } from "./useSelectListBox";

export type SearchableSelectOption = {
  value: string;
  label: string;
};

const Field = styled.div`
  position: relative;
`;

const Input = styled.input<{ $open: boolean }>`
  width: 100%;
  padding: ${spacing.x2}px ${spacing.x3}px ${spacing.x2}px ${spacing.x2}px;
  border: 1px solid
    ${({ $open }) => ($open ? colors.primary : colors.border)};
  border-radius: 4px;
  font-size: ${fontSizes.regular}px;
  color: ${colors.text};
  background-color: ${colors.backgroundLight};
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: ${colors.primary};
  }

  &::placeholder {
    color: ${colors.textMuted};
  }

  &:disabled {
    background-color: ${colors.backgroundDark};
    color: ${colors.textMuted};
    cursor: not-allowed;
    opacity: 0.8;
  }
`;

const Chevron = styled(SelectChevron)`
  position: absolute;
  right: ${spacing.x2}px;
  top: 50%;
  transform: translateY(-50%)
    rotate(${({ $open }) => ($open ? 180 : 0)}deg);
`;

type SearchableSelectProps = {
  value: string;
  options: SearchableSelectOption[];
  onChange: (value: string) => void;
  onBlur?: () => void;
  placeholder?: string;
  emptyOptionLabel?: string;
  noResultsLabel: string;
  id?: string;
  disabled?: boolean;
};

export const SearchableSelect = ({
  value,
  options,
  onChange,
  onBlur,
  placeholder,
  emptyOptionLabel,
  noResultsLabel,
  id,
  disabled,
}: SearchableSelectProps) => {
  const listId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const [draft, setDraft] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const listBox = useSelectListBox(open, inputRef, listRef);

  const allOptions = useMemo(() => {
    if (!emptyOptionLabel) {
      return options;
    }
    return [{ value: "", label: emptyOptionLabel }, ...options];
  }, [emptyOptionLabel, options]);

  const selectedLabel =
    allOptions.find((option) => option.value === value)?.label ?? "";

  const filtered = useMemo(() => {
    const query = filter.trim().toLowerCase();
    if (!query) {
      return allOptions;
    }
    return allOptions.filter((option) =>
      option.label.toLowerCase().includes(query),
    );
  }, [allOptions, filter]);

  const highlightedIndex = Math.min(
    activeIndex,
    Math.max(filtered.length - 1, 0),
  );

  const close = () => {
    setOpen(false);
    setFilter("");
    setDraft(selectedLabel);
    onBlur?.();
  };

  const select = (nextValue: string) => {
    onChange(nextValue);
    setOpen(false);
    setFilter("");
    const nextLabel =
      allOptions.find((option) => option.value === nextValue)?.label ?? "";
    setDraft(nextLabel);
    onBlur?.();
  };

  const openList = () => {
    if (disabled || open) {
      return;
    }
    const selectedIndex = allOptions.findIndex(
      (option) => option.value === value,
    );
    setOpen(true);
    setFilter("");
    setDraft(selectedLabel);
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
    requestAnimationFrame(() => inputRef.current?.select());
  };

  useEffect(() => {
    if (!open) {
      return;
    }
    const handleClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        rootRef.current?.contains(target) ||
        listRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
      setFilter("");
      setDraft(selectedLabel);
      onBlur?.();
    };
    document.addEventListener("mousedown", handleClick);
    return () => {
      document.removeEventListener("mousedown", handleClick);
    };
  }, [open, selectedLabel, onBlur]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const option = listRef.current?.querySelector<HTMLElement>(
      `[data-select-index="${highlightedIndex}"]`,
    );
    option?.scrollIntoView({ block: "nearest" });
  }, [highlightedIndex, open]);

  const displayValue = open ? draft : selectedLabel;
  const activeOption = filtered[highlightedIndex];

  return (
    <SelectRoot ref={rootRef}>
      <Field>
        <Input
          ref={inputRef}
          id={id}
          $open={open}
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          aria-controls={open ? listId : undefined}
          aria-activedescendant={
            open && activeOption ? `${listId}-${highlightedIndex}` : undefined
          }
          disabled={disabled}
          placeholder={placeholder}
          value={displayValue}
          onFocus={openList}
          onClick={openList}
          onBlur={(event) => {
            if (
              rootRef.current?.contains(event.relatedTarget as Node) ||
              listRef.current?.contains(event.relatedTarget as Node)
            ) {
              return;
            }
            if (open) {
              close();
            }
          }}
          onChange={(event) => {
            setOpen(true);
            setDraft(event.target.value);
            setFilter(event.target.value);
            setActiveIndex(0);
          }}
          onKeyDown={(event) => {
            if (event.key === "ArrowDown") {
              event.preventDefault();
              if (!open) {
                openList();
                return;
              }
              setActiveIndex((index) =>
                Math.min(index + 1, Math.max(filtered.length - 1, 0)),
              );
              return;
            }
            if (event.key === "ArrowUp") {
              event.preventDefault();
              setActiveIndex((index) => Math.max(index - 1, 0));
              return;
            }
            if (event.key === "Enter") {
              event.preventDefault();
              const option = filtered[highlightedIndex];
              if (option) {
                select(option.value);
              }
              return;
            }
            if (event.key === "Escape") {
              event.preventDefault();
              setOpen(false);
              setFilter("");
              setDraft(selectedLabel);
            }
          }}
        />
        <Chevron size={14} $open={open} />
      </Field>
      {open &&
        createPortal(
          <SelectList
            ref={listRef}
            id={listId}
            role="listbox"
            $top={listBox.top}
            $left={listBox.left}
            $width={listBox.width}
            $maxHeight={listBox.maxHeight}
          >
            {filtered.length === 0 ? (
              <SelectEmpty>{noResultsLabel}</SelectEmpty>
            ) : (
              filtered.map((option, index) => (
                <li key={option.value || "empty"} role="presentation">
                  <SelectOptionButton
                    id={`${listId}-${index}`}
                    data-select-index={index}
                    type="button"
                    role="option"
                    $active={index === highlightedIndex}
                    $selected={option.value === value}
                    aria-selected={option.value === value}
                    onMouseEnter={() => setActiveIndex(index)}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      select(option.value);
                    }}
                  >
                    {option.label}
                  </SelectOptionButton>
                </li>
              ))
            )}
          </SelectList>,
          document.body,
        )}
    </SelectRoot>
  );
};
